import * as React from 'react';
import LazyLoad from 'react-lazyload';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import { CardMediaContainer } from './cardMediaContainer';
import { FeaturedSection } from './featuredSection';
import { FavoriteAndAvailability } from './favoriteAndAvailability';
import { CardActionArea } from 'gatsby-theme-material-ui';
import Stack from '@mui/material/Stack';
import { IntlProvider, FormattedMessage, FormattedNumberParts } from 'react-intl';
import Divider from '@mui/material/Divider';
import GppGoodIcon from '@mui/icons-material/GppGood';

const Carousel = React.lazy(() => import('react-material-ui-carousel'));
import useTheme from '@mui/material/styles/useTheme';
import ar from './ar.json';
import en from './en.json';
import { LocaleContext } from '../../../contexts/language';

const languages = {
  ar, en
};

const PureOfferCard = ({
  featured,
  stockAvailability,
  favorite,
  onToggleFavorite,
  images,
  link,
  linkTitle,
  title,
  subTitle,
  purchasePrice,
  monthlyPrice,
  currency,
  cardProps,
  providerName,
  providerLogo,
  preTitle,
  exteriorColor,
  interiorColor,
  finalPrice,
  officialDealer
}) => {
  const theme = useTheme();
  const localeConfig = React.useContext(LocaleContext);
  // const [expanded, setExpanded] = React.useState(false);
  return (<IntlProvider locale={localeConfig?.lang} messages={languages[localeConfig?.lang] || languages.en}>
    <Card elevation={featured ? 4 : 1} sx={{ maxWidth: 345, height: '100%' }} {...cardProps}>
      <LazyLoad height={200}>
        <CardMediaContainer>
          <FavoriteAndAvailability
            stockAvailability={stockAvailability}
            favorite={favorite}
            onToggleFavorite={onToggleFavorite}
          />
          {images && (<React.Suspense fallback={<div style={{ height: '200px' }} />}>
            <Carousel
              autoPlay={false}
              indicators
              height={200}
              navButtonsProps={{
                style: {
                  backgroundColor:'#3a3a3c7a',
                  color: theme.palette.primary.main
                }
              }}
              indicatorContainerProps={{
                style: {

                  position: 'relative', marginTop: '-24px', zIndex: 1
                }
              }}
              indicatorIconButtonProps={{
                style: {
                  color: '#c9cdcf'
                }
              }}
              activeIndicatorIconButtonProps={{
                style: {
                  color: theme.palette.primary.main
                }
              }}
              navButtonsAlwaysVisible
              swipe={false}
            >
              {images}
            </Carousel>
          </React.Suspense>)}
          <FeaturedSection featured={featured} />
        </CardMediaContainer>
      </LazyLoad>
      <CardActionArea
        to={link}
        title={linkTitle}
      >
        <CardContent>
          <Stack spacing={2}>
            {preTitle ? (
              <Stack direction={'row'} sx={{ justifyContent: 'center' }}>
                <Typography
                  component="div"
                  sx={{ fontSize: '0.8rem', color: '#A6A6A6', fontWeight: 600 }}
                >
                  {preTitle}
                </Typography>
              </Stack>
            ) : null}
            <Stack direction={'row'} sx={{ justifyContent: 'center' }}>
              <Typography variant="h5" sx={{ fontSize: '1rem', fontWeight: '600', color: 'primary.main' }}
                component="div">
                {/*<Link*/}
                {/*  sx={{ color: 'offerPricingCard.title' }}*/}
                {/*  underline="none"*/}
                {/*  to={link}*/}
                {/*  title={linkTitle}*/}
                {/*  disabled={!link}*/}
                {/*>*/}
                {title}
                {/*</Link>*/}
              </Typography>
            </Stack>
            <Stack
              direction={'row'}
              divider={<Divider orientation="vertical" flexItem />}
              sx={{ justifyContent: 'space-around' }}
              // sx={{ ml: 'auto', mr: 'auto' }}
            >
              <Stack sx={{ alignItems: 'center', width: '50%' }}>
                {purchasePrice && (<>
                  <Typography sx={{ fontWeight: '500', fontSize: '0.7rem', color: 'offerPricingCard.startsFrom' }}>
                    {finalPrice ? <FormattedMessage id={'modelOffer.cashPrice'} defaultMessage={'Cash Price'} /> :
                      <FormattedMessage id={'modelOffer.startsFromPrice'} defaultMessage={'Starts From'} />}
                  </Typography>
                  <Typography sx={{ fontWeight: '600', fontSize: '1.2rem' }} color={'primary'}>
                    <FormattedNumberParts
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                      style={'currency'}
                      currency={currency}
                      value={purchasePrice}
                    >{(parts) => parts.map(({ value, type }, i) => type === 'currency' ?
                        <Box key={i} component="span" sx={{
                          color: 'offerPricingCard.currency', fontSize: '1rem'
                        }}>{value}</Box> : value)}
                    < /FormattedNumberParts>
                  </Typography>
                </>)}
              </Stack>
              <Stack sx={{ alignItems: 'center', width: '50%' }}>
                {monthlyPrice && (<>
                  <Typography sx={{ fontWeight: '500', fontSize: '0.7rem', color: 'offerPricingCard.startsFrom' }}>
                    <FormattedMessage id={'modelOffer.monthlyFromPrice'} defaultMessage={'Monthly From'} />
                  </Typography>
                  <Typography sx={{ fontWeight: '600', fontSize: '1.2rem' }} color={'primary'}>
                    <FormattedNumberParts
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                      style={'currency'}
                      currency={currency}
                      value={monthlyPrice}
                    >{(parts) => parts.map(({ value, type }, i) => type === 'currency' ? <Box component="span" sx={{
                        color: 'offerPricingCard.currency', fontSize: '1rem'
                      }} key={i}>{value}</Box> : value)}
                    < /FormattedNumberParts>
                  </Typography>
                </>)}
              </Stack>
            </Stack>

            {subTitle && (
              <Typography gutterBottom variant="h6" sx={{ fontSize: '0.9rem', fontWeight: '600' }} component="div">
                {subTitle}
              </Typography>)}

            {(interiorColor || exteriorColor) ? (
              <>
                <Divider orientation="horizontal" flexItem />
                <Stack spacing={0} sx={{ alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                    <FormattedMessage id={'offerCard.colorHeader'} defaultMessage={'Colour'} />
                  </Typography>
                  <Stack spacing={1} direction={'row'} sx={{ justifyContent: 'space-around', width:'100%' }}>
                    {exteriorColor ? (
                      <Typography sx={{ fontSize: '0.8rem', textAlign: 'center', width: '50%'  }}>{exteriorColor}</Typography>
                    ) : null}
                    {interiorColor ? (
                      <Typography sx={{ fontSize: '0.8rem' ,  textAlign: 'center', width: '50%'  }}>{interiorColor}</Typography>
                    ) : null}
                  </Stack>
                </Stack>
              </>) : null}

            {(providerLogo||providerName) ? (
              <>
                <Divider orientation="horizontal" flexItem />
                <Stack spacing={0} sx={{ alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                    <FormattedMessage
                      id={'offerCard.soldBy'}
                      defaultMessage={'Offer From'}
                    />
                  </Typography>
                  {providerLogo||providerName}
                </Stack>
              </>
            ) : null}
            {officialDealer ? (
              <>
                <Divider orientation="horizontal" flexItem />
                <Stack spacing={0} sx={{ alignItems: 'center' }}>
                  <Stack direction={'row'} sx={{ alignItems: 'center' }}>
                    <GppGoodIcon color={'success'} />
                    <Typography sx={{ fontSize: '0.8rem', frontWeight: 400 }}>
                      <FormattedMessage
                        defaultMessage={'Official {officialDealer} dealership'}
                        id={'modelOffer.officialDealer'}
                        values={{ officialDealer }}
                      />
                    </Typography>
                  </Stack>

                </Stack>
              </>
            ) : null}

          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  </IntlProvider>
  );


};

PureOfferCard.propTypes = {
  currency: PropTypes.string,
  monthlyPrice: PropTypes.number,
  purchasePrice: PropTypes.number,
  preTitle: PropTypes.node,
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string,
  link: PropTypes.string,
  linkTitle: PropTypes.string,
  images: PropTypes.arrayOf(PropTypes.node),
  featured: PropTypes.bool,
  favorite: PropTypes.bool,
  onToggleFavorite: PropTypes.func,
  stockAvailability: PropTypes.oneOf(['UNKNOWN', 'IN_STOCK', 'WAIT_LIST', 'NOT_AVAILABLE']),
  cardProps: PropTypes.shape({}),
  providerName: PropTypes.node,
  providerLogo: PropTypes.node,
  interiorColor: PropTypes.string,
  interiorColorCode: PropTypes.string,
  exteriorColorCode: PropTypes.string,
  exteriorColor: PropTypes.string,
  finalPrice: PropTypes.bool,
  officialDealer: PropTypes.node
};
PureOfferCard.defaultProps = {
  featured: false, favorite: false, stockAvailability: 'UNKNOWN', cardProps: {}, finalPrice: false
};

export default PureOfferCard;
export { PureOfferCard };